import { useContext, useEffect, useMemo, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import style from '@aytm/app/stats/page/comments/styles/index.module.scss'
import classNames from 'classnames'
import { range } from '@aytm/app/stats/page/comments/lib/utils'

import { OEContext } from '@aytm/app/stats/page/comments'

import Button from '@aytm/lib/rfd-ui/components/basic/button'

import Fade from '@aytm/app/stats/page/comments/comments/components/Fade'
import Response from '@aytm/app/stats/page/comments/comments/Response'

import scrollToElement from 'scroll-to-element'

const PageDataQA = ({
  reportId,
  responsesOptions,
  showTraitsRef,
  responseOrderRef,
  popupNotificationsDisabled,
  isShowHiddenFilters,
  onCloseNotification,
  onPageChange
}) => {
  const { searchState, commentStatusesState } = useContext(OEContext)
  const { request, isLoading, isProcessing, isQALoading, qaProcessedResponseId } = searchState
  const { reject } = commentStatusesState
  const { pageSize, currentPage } = request

  const isOnePerPage = useMemo(() => 1 == pageSize, [pageSize])

  const onApproveAll = () => {
    // collecting uids to approve
    const uidsToApprove = responsesOptions.pageData.reduce((ret, item) => {
      ret.push({
        uid: item.response.uid.toString(36),
        isRejected: reject[item.response.uid] || 'true' == reject[item.response.uid]
      })
      return ret
    }, [])

    if (onPageChange && 'function' == typeof onPageChange) {
      onPageChange(currentPage + 1, uidsToApprove)
    }
    scrollToElement('.-header', {
      offset: 0,
      align: 'top',
      duration: 250
    })
  }
  const onApproveAllRef = useRef()
  onApproveAllRef.current = onApproveAll

  const onKeyDownHandler = useCallback(event => {
    if ((49 == event.keyCode || 97 == event.keyCode || 35 == event.keyCode) && event.shiftKey) {
      onApproveAllRef.current()
    }
  }, [])

  useEffect(() => {
    if (!isOnePerPage) window.addEventListener('keydown', onKeyDownHandler)
    else window.removeEventListener('keydown', onKeyDownHandler)

    return () => {
      window.removeEventListener('keydown', onKeyDownHandler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnePerPage])

  const renderResponse = item => (
    <div key={`PageDataQAResponse_${item.response.uid}`}>
      <Response
        dataId={`Response_${item.response.uid}`}
        reportId={reportId}
        survey={responsesOptions.survey}
        reportVirtual={responsesOptions.reportVirtual}
        reportSchema={responsesOptions.reportSchema}
        response={item}
        showTraitsRef={showTraitsRef}
        responseOrderRef={responseOrderRef}
        popupNotificationsDisabled={popupNotificationsDisabled}
        isShowHiddenFilters={isShowHiddenFilters}
        onCloseNotification={onCloseNotification}
      />
    </div>
  )

  return (
    <>
      <div className={classNames(style['responses'], style['is-qa'])}>
        {responsesOptions.pageData[0] ? (
          isOnePerPage ? (
            <Fade show={!qaProcessedResponseId || qaProcessedResponseId !== responsesOptions.pageData[0].response.uid}>
              {renderResponse(responsesOptions.pageData[0])}
            </Fade>
          ) : (
            responsesOptions.pageData.map(item => renderResponse(item))
          )
        ) : (
          <div className={style['no-results']}>{!isLoading && !isProcessing ? 'All done!' : 'Loading...'}</div>
        )}
        {(isQALoading || (!isOnePerPage && (isLoading || isProcessing))) && (
          <div
            className={classNames(style['status-loader'], {
              [style['status-loader-static']]: !responsesOptions.pageData || !responsesOptions.pageData.length
            })}
          >
            <span className={`afd-loader afd-loader-long`}>
              {range(0, 80).map(id => (
                <span key={`loading-span-${id}`} />
              ))}
            </span>
          </div>
        )}
      </div>
      {!isOnePerPage ? (
        <>
          <div className={style['responses-bottom-paginaton']}>
            <div className={style['right']}>
              {!isLoading && !isProcessing && responsesOptions.pageData.length ? (
                <Button
                  label="APPROVE ALL"
                  disabled={isLoading || isProcessing}
                  onClick={!isLoading && !isProcessing ? onApproveAll : null}
                />
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </>
  )
}

PageDataQA.propTypes = {
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  survey: PropTypes.object,
  popupNotificationsDisabled: PropTypes.object,
  responsesOptions: PropTypes.object,
  responsesRef: PropTypes.object,
  showTraitsRef: PropTypes.object,
  responseOrderRef: PropTypes.object,
  isShowHiddenFilters: PropTypes.bool,
  onCloseNotification: PropTypes.func,
  onPageChange: PropTypes.func
}

PageDataQA.displayName = 'PageDataQA'

export default PageDataQA
